import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "./NavBar.css";
import logo from "../assets/logo.png";

function NavBar() {
  const [navOpen, setNavOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const userData =
    token && localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;

  const isTherapist = userData?.role === "THERAPIST";
  const isAdmin =
    userData?.role === "OFFICE_ADMIN" || userData?.role === "PORTAL_ADMIN";

  const roleLabel =
    userData?.role === "PORTAL_ADMIN" ? "Portal Admin"
    : userData?.role === "OFFICE_ADMIN" ? "Office Admin"
    : userData?.role === "THERAPIST" ? "Therapist"
    : userData?.role || "";

  const firstName = userData?.first_name || "";
  const lastName = userData?.last_name || "";
  const licenseType = userData?.license_type || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const displayName = licenseType ? `${fullName}, ${licenseType}` : fullName;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const toggleDropdown = (name) =>
    setOpenDropdown((prev) => (prev === name ? null : name));

  const closeAll = () => {
    setNavOpen(false);
    setOpenDropdown(null);
  };

  // Close on outside click
  useEffect(() => {
    function handleOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setNavOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Inline dropdown component — shares parent scope for state/handlers
  function Dropdown({ name, label, children }) {
    const isOpen = openDropdown === name;
    return (
      <div className="nav-dropdown">
        <button
          className={`nav-dropbtn${isOpen ? " open" : ""}`}
          onClick={() => toggleDropdown(name)}
          aria-expanded={isOpen}
        >
          {label}
          <span className={`nav-chevron${isOpen ? " up" : ""}`} aria-hidden>›</span>
        </button>
        {isOpen && (
          <div className="nav-dropdown-content" role="menu">
            {children}
          </div>
        )}
      </div>
    );
  }

  return (
    <header className="navbar" ref={navRef}>
      {/* Brand */}
      <div className="nav-brand">
        <NavLink to="/" className="nav-brand-link" onClick={closeAll}>
          <img src={logo} alt="Avenues Portal" className="nav-logo" />
          <span className="nav-brand-text">Avenues Portal</span>
        </NavLink>
      </div>

      {/* Hamburger (mobile) */}
      <button
        className="nav-hamburger"
        onClick={() => { setNavOpen(!navOpen); setOpenDropdown(null); }}
        aria-label="Toggle navigation menu"
        aria-expanded={navOpen}
      >
        <span className={`hline${navOpen ? " open" : ""}`} />
        <span className={`hline${navOpen ? " open" : ""}`} />
        <span className={`hline${navOpen ? " open" : ""}`} />
      </button>

      {/* Navigation links */}
      <nav className={`nav-links${navOpen ? " open" : ""}`}>

        {/* Not logged in */}
        {!token && (
          <NavLink
            to="/" end
            className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
            onClick={closeAll}
          >
            Login
          </NavLink>
        )}

        {/* ── THERAPIST ── */}
        {token && isTherapist && (
          <>
            <NavLink
              to="/therapist/dashboard"
              className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
              onClick={closeAll}
            >
              Dashboard
            </NavLink>

            <Dropdown name="clients" label="My Clients">
              <NavLink to="/therapist/client-profile" className="ddlink" onClick={closeAll} role="menuitem">
                Client Profiles
              </NavLink>
              <NavLink to="/therapist/notes" className="ddlink" onClick={closeAll} role="menuitem">
                Session Notes
              </NavLink>
            </Dropdown>

            <Dropdown name="sessions" label="My Sessions">
              <NavLink to="/therapist/daily-log-multi" className="ddlink" onClick={closeAll} role="menuitem">
                Log Sessions
              </NavLink>
              <NavLink to="/therapist/billing-history" className="ddlink" onClick={closeAll} role="menuitem">
                Billing History
              </NavLink>
              <NavLink to="/therapist/debt" className="ddlink" onClick={closeAll} role="menuitem">
                Owed Sessions
              </NavLink>
            </Dropdown>

            <Dropdown name="finance" label="Finance">
              <NavLink to="/therapist/pay-history" className="ddlink" onClick={closeAll} role="menuitem">
                Pay History
              </NavLink>
            </Dropdown>

            <Dropdown name="tools" label="Tools & More">
              <NavLink to="/therapist/messages" className="ddlink" onClick={closeAll} role="menuitem">
                Messages
              </NavLink>
              <NavLink to="/therapist/tools" className="ddlink" onClick={closeAll} role="menuitem">
                Tools
              </NavLink>
              <NavLink to="/therapist/resources" className="ddlink" onClick={closeAll} role="menuitem">
                Resources
              </NavLink>
            </Dropdown>
          </>
        )}

        {/* ── ADMIN ── */}
        {token && isAdmin && (
          <>
            <NavLink
              to="/admin/dashboard"
              className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
              onClick={closeAll}
            >
              Dashboard
            </NavLink>

            <Dropdown name="clients" label="Clients">
              <NavLink to="/admin/clients" className="ddlink" onClick={closeAll} role="menuitem">
                Client Profiles
              </NavLink>
              <NavLink to="/admin/invoice" className="ddlink" onClick={closeAll} role="menuitem">
                Invoices
              </NavLink>
              <NavLink to="/admin/intake-link" className="ddlink" onClick={closeAll} role="menuitem">
                New Client Intake
              </NavLink>
            </Dropdown>

            <Dropdown name="collections" label="Collections">
              <NavLink to="/admin/payment" className="ddlink" onClick={closeAll} role="menuitem">
                Payment Processing
              </NavLink>
              <NavLink to="/admin/debt-records" className="ddlink" onClick={closeAll} role="menuitem">
                Debt Records
              </NavLink>
              <NavLink to="/admin/daily-report" className="ddlink" onClick={closeAll} role="menuitem">
                Daily Report
              </NavLink>
            </Dropdown>

            <Dropdown name="finance" label="Finance">
              <NavLink to="/admin/payroll" className="ddlink" onClick={closeAll} role="menuitem">
                Payroll
              </NavLink>
              <NavLink to="/admin/bonuses" className="ddlink" onClick={closeAll} role="menuitem">
                Bonuses
              </NavLink>
            </Dropdown>

            <Dropdown name="team" label="Team">
              <NavLink to="/admin/users" className="ddlink" onClick={closeAll} role="menuitem">
                Users
              </NavLink>
              <NavLink to="/admin/new-user" className="ddlink" onClick={closeAll} role="menuitem">
                Add User
              </NavLink>
            </Dropdown>

            <Dropdown name="tools" label="Tools & More">
              <NavLink to="/admin/messages" className="ddlink" onClick={closeAll} role="menuitem">
                Messages
              </NavLink>
              <NavLink to="/admin/tools" className="ddlink" onClick={closeAll} role="menuitem">
                Tools
              </NavLink>
              <NavLink to="/admin/server" className="ddlink" onClick={closeAll} role="menuitem">
                Server &amp; Portal Info
              </NavLink>
              <NavLink to="/admin/resources" className="ddlink" onClick={closeAll} role="menuitem">
                Resources
              </NavLink>
            </Dropdown>
          </>
        )}
      </nav>

      {/* User identity + logout */}
      {token && (
        <div className="nav-user-area">
          {displayName && (
            <div className="nav-user-info">
              <span className="nav-user-name">{displayName}</span>
              <span className="nav-user-role">{roleLabel}</span>
            </div>
          )}
          <button className="nav-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      )}
    </header>
  );
}

export default NavBar;
